import { Badge, Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useShippingWorkbooks } from '@resistapp/client/hooks/api';
import type { ShippingWorkbook } from '@resistapp/common/api-types';
import { chain } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { AdminShipmentsSection } from '../admin-view/admin-shipments-view';

export function UserShippingView() {
  const { user, userLoading } = useUser();
  const [showWorkbooksWithoutQuota, setShowWorkbooksWithoutQuota] = useState(false);
  const {
    data,
    isPending: areWorkbooksPending,
    error: workbooksError,
  } = useShippingWorkbooks({
    enabled: !userLoading && Boolean(user),
  });

  const shippingWorkbooks = useMemo(
    () =>
      chain(data?.workbooks ?? [])
        .sortBy(workbook => getShippingWorkbookDisplayName(workbook).toLowerCase())
        .value(),
    [data?.workbooks],
  );
  const workbooksWithQuotaRemaining = useMemo(
    () => shippingWorkbooks.filter(workbook => workbook.shippingRequestsAvailable > 0),
    [shippingWorkbooks],
  );
  const visibleShippingWorkbooks = showWorkbooksWithoutQuota ? shippingWorkbooks : workbooksWithQuotaRemaining;

  if (userLoading || areWorkbooksPending) {
    return <LoadingView />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (workbooksError) {
    return (
      <Box padding={theme.spacing[6]}>
        <MaybeApiError error={workbooksError} />
      </Box>
    );
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Sample shipping" $extraWide={true} />
      <VStack align="stretch" spacing={6} padding={theme.spacing[4]}>
        <ShippingWorkbookShortcuts
          shippingWorkbooks={shippingWorkbooks}
          visibleShippingWorkbooks={visibleShippingWorkbooks}
          showWorkbooksWithoutQuota={showWorkbooksWithoutQuota}
          onToggleShowWorkbooksWithoutQuota={() => {
            setShowWorkbooksWithoutQuota(value => !value);
          }}
        />
        <AdminShipmentsSection scope="user" />
      </VStack>
    </div>
  );
}

function getShippingWorkbookDisplayName(workbook: ShippingWorkbook): string {
  return workbook.name?.trim() || workbook.sampleSheetName?.trim() || 'Untitled workbook';
}

function ShippingWorkbookShortcuts({
  shippingWorkbooks,
  visibleShippingWorkbooks,
  showWorkbooksWithoutQuota,
  onToggleShowWorkbooksWithoutQuota,
}: {
  shippingWorkbooks: ShippingWorkbook[];
  visibleShippingWorkbooks: ShippingWorkbook[];
  showWorkbooksWithoutQuota: boolean;
  onToggleShowWorkbooksWithoutQuota: () => void;
}) {
  const workbooksWithoutQuotaCount = shippingWorkbooks.filter(
    workbook => workbook.shippingRequestsAvailable <= 0,
  ).length;

  return (
    <VStack align="stretch" spacing={4}>
      <Flex align="center" justify="space-between" gap={theme.spacing[3]} wrap="wrap">
        <Text fontSize="lg" fontWeight={600}>
          Workbooks with shipping quota
        </Text>
        {shippingWorkbooks.length > 0 && workbooksWithoutQuotaCount > 0 ? (
          <Button size="sm" variant="outline" onClick={onToggleShowWorkbooksWithoutQuota}>
            {showWorkbooksWithoutQuota
              ? 'Hide workbooks without quota'
              : `Show ${workbooksWithoutQuotaCount} without quota`}
          </Button>
        ) : null}
      </Flex>
      {shippingWorkbooks.length === 0 ? (
        <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
          <Text color="gray.600">No lab workbooks are available for shipping requests.</Text>
        </Box>
      ) : visibleShippingWorkbooks.length === 0 ? (
        <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
          <Text color="gray.600">No lab workbooks have shipping quota remaining.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={3}>
          {visibleShippingWorkbooks.map(workbook => (
            <ShippingWorkbookRow key={workbook.id} workbook={workbook} />
          ))}
        </VStack>
      )}
    </VStack>
  );
}

function ShippingWorkbookRow({ workbook }: { workbook: ShippingWorkbook }) {
  const available = workbook.shippingRequestsAvailable;
  const canRequest = available > 0;

  return (
    <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
      <HStack justify="space-between" align="center" spacing={4} wrap="wrap">
        <Box minW={0}>
          <Text fontWeight={600}>{getShippingWorkbookDisplayName(workbook)}</Text>
          <Text fontSize="sm" color="gray.600">
            {workbook.organisation.name}
          </Text>
        </Box>
        <HStack spacing={2} wrap="wrap">
          <Badge colorScheme={available > 0 ? 'green' : 'red'}>Available: {available}</Badge>
          <Badge colorScheme="blue">Pending: {workbook.shippingRequestsPending}</Badge>
          <Badge colorScheme="purple">Spent: {workbook.shippingRequestsUsed}</Badge>
          {canRequest ? (
            <Button as={RouterLink} to={`/admin/sample-shipping/workbook/${workbook.id}`} colorScheme="blue" size="sm">
              Request shipping
            </Button>
          ) : (
            <Button size="sm" isDisabled={true}>
              No quota remaining
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );
}
